import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Avatar,
  IconButton,
  Paper,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import {
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  WbSunny,
  WbTwilight,
  Nightlight,
  Flare,
  AccessAlarm,
} from '@mui/icons-material';
import moment from 'moment';
import TimeGridRow from '../../TimeGridRow/TimeGridRow';
import { useToast } from '../ToastProvider/ToastProvider';

interface AvailabilitySubmissionModalProps {
  open: boolean;
  currentDate: string;
  availability: Record<string, any>; // Array of time ranges, e.g., ["01:23-02:23", "05:00-06:00"]
  defaultAvailabilities: Record<string, any>[],
  setShowDefaultAvailabilityModal: (value: boolean) => void;
  onClose: () => void;
  onSubmit: (newAvailability: Record<string, any>, currentDate: string) => Promise<void>;
}

const AvailabilitySubmissionModal: React.FC<AvailabilitySubmissionModalProps> = ({
  open,
  currentDate,
  availability,
  defaultAvailabilities,
  setShowDefaultAvailabilityModal,
  onClose,
  onSubmit,
}) => {
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null as string | null);
  const [newAvailability, setNewAvailability] = useState<Record<string, any>>({});
  const [mergedAvailability, setMergedAvailability] = useState<string[]>([]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit(newAvailability, currentDate);
      onClose();
    } catch (error) {
      console.error('Error submitting availability:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  function areMapsEqual(map1: Record<string, any>, map2: Record<string, any>) {
    // 1. Check if sizes are equal
    if (Object.keys(map1).length !== Object.keys(map2).length) {
      return false;
    }

    // 2. Iterate through one map and compare key-value pairs
    for (const [key, value] of Object.entries(map1)) {
      // Check if the key exists in the second map and if values are equal
      if (!map2.hasOwnProperty(key) || map2[key] !== value) {
        return false;
      }
    }

    // If all checks pass, the maps are considered equal
    return true;
  }

  const handleClose = () => {
    if (!isSubmitting) {
      setNewAvailability(JSON.parse(JSON.stringify(availability)));
      setMergedAvailability(JSON.parse(JSON.stringify(mergeAvailability(Object.values(availability).filter((v: string) => v !== '')))));
      setSelectedDay(null);
      onClose();
    }
  };

  const mergeAvailability = (availability: string[]): string[] => {
    const sortedRanges = availability
      .map((range) => {
        const [start, end] = range.split('-');
        return { start: moment(start, 'HH:mm'), end: moment(end, 'HH:mm') };
      })
      .sort((a, b) => a.start.diff(b.start));

    const mergedRanges: { start: moment.Moment; end: moment.Moment }[] = [];
    for (const range of sortedRanges) {
      if (
        mergedRanges.length > 0 &&
        mergedRanges[mergedRanges.length - 1].end.isSameOrAfter(range.start)
      ) {
        mergedRanges[mergedRanges.length - 1].end = moment.max(
          mergedRanges[mergedRanges.length - 1].end,
          range.end
        );
      } else {
        mergedRanges.push(range);
      }
    }

    return mergedRanges.map(
      (range) =>
        `${range.start.format('HH:mm')}-${range.end.format('HH:mm')}`
    );
  };

  const handlePreselectAvailability = (preselectType: string) => {
    const updatedAvailability: Record<string, any> = {};
    for (let hour = 0; hour < 24; hour++) {
      let newRange: string = hour < 10 ? `0${hour}:00-${hour + 1 < 10 ? `0${hour + 1}` : hour + 1}:00` : `${hour}:00-${hour + 1}:00`;
      switch (preselectType) {
        case "9/5":
          if (hour >= 9 && hour < 17) {
            updatedAvailability[hour] = newRange;
          }
          break;
        case "morning":
          if (hour >= 6 && hour < 12) {
            updatedAvailability[hour] = newRange;
          }
          break;
        case "afternoon":
          if (hour >= 12 && hour < 17) {
            updatedAvailability[hour] = newRange;
          }
          break;
        case "evening":
          if (hour >= 17 && hour < 21) {
            updatedAvailability[hour] = newRange;
          }
          break;
        case "night":
          if (hour >= 21 || hour < 6) {
            updatedAvailability[hour] = newRange;
          }
          break;
      }

    }
    setNewAvailability(updatedAvailability);
    setMergedAvailability(mergeAvailability(Object.values(updatedAvailability).filter((v: string) => v !== '')));
  }

  React.useEffect(() => {
    defaultAvailabilities.map((v: Record<string, any>) => {
      // compare map v.availability to availability
      if (areMapsEqual(v.availability, availability)) {
        setSelectedDay(v.dayUserId.split("#")[1]);
      }
    })
    setNewAvailability(JSON.parse(JSON.stringify(availability)));
    setMergedAvailability(JSON.parse(JSON.stringify(mergeAvailability(Object.values(availability).filter((v: string) => v !== '')))));
  }, [])

  React.useEffect(() => {
    if (open) {
      setSelectedDay(null);
      defaultAvailabilities.map((v: Record<string, any>) => {
        // compare map v.availability to availability
        if (areMapsEqual(v.availability, newAvailability)) {
          setSelectedDay(v.dayUserId.split("#")[1]);
        }
      })
    }
  }, [open, newAvailability])

  React.useEffect(() => {
    setNewAvailability(JSON.parse(JSON.stringify(availability)));
    setMergedAvailability(JSON.parse(JSON.stringify(mergeAvailability(Object.values(availability).filter((v: string) => v !== '')))));
  }, [availability])

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
              <CalendarIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Update Your Availability
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {moment(currentDate).format('dddd, MMMM D, YYYY')}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleClose} disabled={isSubmitting}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Time Slot Legend */}
          <Paper variant="elevation" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Availability Preselects
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', marginBottom: 1 }}>
              <Button variant='contained' color="success" onClick={(() => handlePreselectAvailability("9/5"))}>
                <AccessAlarm sx={{ mr: 1, color: "background.default" }} />
                <Typography variant="body2" sx={{ color: "background.default" }}> 9/5 </Typography>
              </Button>
              <Button variant='contained' color="info" onClick={(() => handlePreselectAvailability("morning"))}>
                <Flare sx={{ mr: 1, color: "background.default" }} />
                <Typography variant="body2" sx={{ color: "background.default" }}> Morning </Typography>
              </Button>
              <Button variant='contained' color="warning" onClick={(() => handlePreselectAvailability("afternoon"))}>
                <WbSunny sx={{ mr: 1, color: "background.default" }} />
                <Typography variant="body2" sx={{ color: "background.default" }}> Afternoon </Typography>
              </Button>
              <Button variant='contained' color="primary" onClick={(() => handlePreselectAvailability("evening"))}>
                <WbTwilight sx={{ mr: 1, color: "background.default" }} />
                <Typography variant="body2" sx={{ color: "background.default" }}> Evening </Typography>
              </Button>
              <Button variant='contained' color="secondary" onClick={(() => handlePreselectAvailability("night"))}>
                <Nightlight sx={{ mr: 1, color: "background.default" }} />
                <Typography variant="body2" sx={{ color: "background.default" }}> Night </Typography>
              </Button>
            </Box>
            <Divider sx={{ mb: 1 }} />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', marginBottom: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Availability Defaults
              </Typography>
              {
                Array.from({ length: 7 }, (_, day) => {
                  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                  return (
                    <Chip
                      key={day}
                      label={dayNames[day]}
                      sx={{ bgcolor: selectedDay === dayNames[day] ? 'info.light' : 'grey.300', color: 'text.primary', cursor: 'pointer', '&:hover': { bgcolor: 'grey.400' } }}
                      onClick={(() => {
                        setSelectedDay(dayNames[day]);
                        const defaultAvailabilityForDay = defaultAvailabilities.find((v: Record<string, any>) => v.dayUserId.split("#")[1] === dayNames[day]);
                        if (defaultAvailabilityForDay) {
                          setNewAvailability(JSON.parse(JSON.stringify(defaultAvailabilityForDay.availability)));
                          setMergedAvailability(JSON.parse(JSON.stringify(mergeAvailability(Object.values(defaultAvailabilityForDay.availability as Record<string, string>).filter((v) => v !== '')))));
                        } else {
                          toast.showWarning(`No default availability set for ${dayNames[day]}`);
                        }
                      })}
                    />
                  );
                })
              }
              <Chip
                key={'edit'}
                label={'Edit Defaults'}
                color='primary'
                sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'primary.dark' } }}
                onClick={() => setShowDefaultAvailabilityModal(true)}
              />
            </Box>
            <Divider sx={{ mb: 1 }} />
            <Stack sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1 }}>
              {Array.from({ length: 24 }, (_, hour) => {
                return (<TimeGridRow key={hour}
                  hour={hour}
                  availability={newAvailability[hour]}
                  mergedAvailability={mergedAvailability}
                  selectedDay={selectedDay}
                  onClick={(newState, newRange) => {
                    setSelectedDay(null);
                    newAvailability[hour] = newState === 'unavailable' ? '' : newRange;
                    setMergedAvailability(mergeAvailability(Object.values(newAvailability).filter((v: string) => v !== '')));
                    setNewAvailability(JSON.parse(JSON.stringify(newAvailability)));
                    defaultAvailabilities.map((v: Record<string, any>) => {
                      // compare map v.availability to availability
                      if (areMapsEqual(v.availability, newAvailability)) {
                        setSelectedDay(v.dayUserId.split("#")[1]);
                      }
                    })
                  }}
                />
                )
              })}
            </Stack>
          </Paper>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleClose} variant="outlined" disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting}
          startIcon={isSubmitting ? null : <CheckCircleIcon />}
        >
          {isSubmitting ? 'Submitting...' : 'Save Availability'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AvailabilitySubmissionModal;